import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FieldWrap } from "@/components/Field";
import { api, errMsg } from "@/lib/api";

const CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
const genPassword = () => Array.from(crypto.getRandomValues(new Uint32Array(10)), (n) => CHARS[n % CHARS.length]).join("");

export function ResetDialog({ target, onClose, onDone }) {
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (target) { setPw(genPassword()); } }, [target]);

  const reset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/users/${target.id}/reset`, { password: pw });
      toast.success(`${target.name} was reset. Give them the temporary password.`);
      onDone();
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="reset-dialog">
        <DialogHeader>
          <DialogTitle>Reset {target?.name}</DialogTitle>
          <DialogDescription>Sets a new temporary password, unlocks the account if it was locked after wrong passwords, and signs them out everywhere. They will choose their own password at their next login.</DialogDescription>
        </DialogHeader>
        <form onSubmit={reset} className="space-y-4">
          <FieldWrap label="Temporary password (6+ characters)">
            <div className="flex gap-2">
              <Input value={pw} onChange={(e) => setPw(e.target.value)} minLength={6} maxLength={128} required className="h-11 bg-white font-mono text-slate-900" data-testid="reset-password-input" />
              <Button type="button" variant="outline" onClick={() => setPw(genPassword())} data-testid="reset-generate-btn" aria-label="Make a new password"><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </FieldWrap>
          <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Write this password down or copy it before you press Reset.</p>
          <Button type="submit" disabled={busy || pw.length < 6} className="w-full" data-testid="reset-submit-btn">Reset account</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
